// Procedural playmat UI overlay generator. Builds a single
// THREE.CanvasTexture composited from:
//
//   1. Procedural grid lines drawn from spawnConfig.gameGrid (the
//      real grid coords the dev calibrated to the painted playmat.
//      NOT DEFAULT_GRID). Dashed style draws inner connecting
//      dashed lines; dotted style draws the outer border + ornament
//      intersections only.
//
//   2. SVG components positioned by world coords from spawnConfig:
//      • atlas / spellbook / cemetery. Centered on the matching
//        spawn point. Rendered for BOTH P1 and P2 (with the P2 copy
//        rotated 180° so it reads right from P2's seat).
//      • life-tracker / mana-tracker / affinity-tracker. Centered
//        on the centroid of the per-player tracker button
//        positions. Rendered for both players.
//
//   3. Cell numbers. Sparse (every other) or all, centered in each
//      grid cell. Recolored per `uiColor`.
//
//   4. Intersection ornaments. `/rune-divider.webp` drawn at every
//      grid intersection, baked color preserved.
//
// World → canvas mapping mirrors the texture UV bake in
// tableScene.js (u = x/TABLE_WIDTH + 0.5, v = 0.5 - z/TABLE_HEIGHT),
// so a world coord matches the same physical spot on the painted
// playmat that the spawn config was calibrated against.
//
// Cache: by config hash (everything settings-driven). Spawn config
// is a static module-level constant so it doesn't enter the cache
// key; if you ever change spawnConfig.js, restart the dev session.

import * as THREE from 'three';
import { computeGridPositions, getGridPoint, DEFAULT_GRID } from './grid';
import { getSpawnConfigSync } from './spawnConfig';
import { COMPONENT_KEYS } from './playmatComponentEditor';
import { getLocalApiOrigin } from '../localApi';

// Authored placements. Set via the in-game Component Editor
// (Game Menu → Set Component Positions → drag → Copy JSON).
// Overrides spawn-config-derived defaults for keys present here.
// Coordinates are world-space (matching spawnConfig.gameGrid +
// tracker positions). Re-author by re-opening the editor.
export const COMPONENT_PLACEMENTS = {
  affinityP1: { x: -86.48, z: 40.32 },
  affinityP2: { x:  86.56, z: -39.85 },
  atlas:      { x:  87.30, z:   4.70 },
  atlas2:     { x: -87.00, z:  -5.00 },
  cemetery:   { x:  87.10, z:  48.20 },
  cemetery2:  { x: -86.40, z: -47.30 },
  lifeP1:     { x:  29.06, z:  64.25 },
  lifeP2:     { x: -29.18, z: -63.82 },
  manaP1:     { x: -29.04, z:  64.34 },
  manaP2:     { x:  29.09, z: -63.86 },
  spellbook:  { x:  87.00, z:  24.80 },
  spellbook2: { x: -86.80, z: -24.80 },
};

const CANVAS_W = 10300;
const CANVAS_H = 7200;

// Raster resolution budget for playmat-scale textures (this UI overlay and
// the battlemap/playmat art in tableScene). The mat only ever reaches the
// screen through the board's drawing buffer, so texels beyond what that
// buffer can resolve at close camera zoom are pure memory waste. Budget =
// the largest possible drawing-buffer width (longest screen edge × the
// renderer's 1.5 pixel-ratio cap) × 4 zoom headroom. the same texel-per-
// screen-pixel density the fixed 10300px canvas gives a 2560px-wide desktop
// today. Desktops land at the native asset size; a phone lands around half,
// which its screen cannot tell apart.
const PLAYMAT_ZOOM_HEADROOM = 4;
export function playmatTexelBudget() {
  if (typeof window === 'undefined') return CANVAS_W;
  const s = window.screen || {};
  const longEdge = Math.max(s.width || 0, s.height || 0) || 1600;
  const bufferCap = longEdge * Math.min(window.devicePixelRatio || 1, 1.5);
  return Math.max(2048, Math.min(CANVAS_W, Math.round(bufferCap * PLAYMAT_ZOOM_HEADROOM)));
}
const TABLE_WIDTH = 200;
const TABLE_HEIGHT = Math.round(200 / (10300 / 7200));

const OUTER_BORDER_WIDTH = 8;
const INNER_LINE_WIDTH = 8;
const CELL_NUMBER_FONT_PX = 160;
// Multiplier applied to grid stroke widths when the "Thick Lines"
// player setting is on. 1.75 reads as a deliberately heavier line
// without crowding the cell.
const THICK_LINE_MULT = 1.75;
// Multiplier applied to the cell-number font size when the
// "Large Numbers" player setting is on. Independent of stroke width
// so the two scales can be toggled separately.
const LARGE_NUMBER_MULT = 1.75;
// Outer-border segments render as two long ticks emanating from
// each endpoint (corner or border T-junction) toward the middle,
// gap remaining for the cell number on the relevant segment.
const TICK_FRACTION = 0.40;
// From every border T-junction, a short perpendicular stick extends
// INTO the grid. Half the length of a normal outer-border tick so
// the T-shape's vertical/horizontal arm reads shorter than its
// horizontal/vertical arms along the border itself.
const TICK_FRACTION_HALF = 0.20;
// Length of the centered dash between two interior ornaments (and
// between a border T-junction's inward stick and the next ornament).
// Always sits at the geographic midpoint of the segment.
const CENTERED_DASH_FRACTION = 0.20;
// Project's medieval display font. Loaded via @font-face in
// src/index.css (../public/fonts/fantaisie-artistique.ttf). Used
// app-wide for headings via the .arena-heading class. Canvas 2D
// reads fonts the same way the DOM does, so the family name
// matches the @font-face declaration. Fallback to serif if the
// custom font hasn't loaded yet (mitigated by the
// document.fonts.ready await in generatePlaymatUiTexture below).
const CELL_NUMBER_FONT_FAMILY = '"FantaisieArtistique", serif';

// SVG (not the 100×100 webp) so it rasterizes crisp at the
// 500 px draw size. The webp's 5× upscale was visibly soft next to
// the natively-rendered grid strokes.
const INTERSECTION_ORNAMENT_URL = '/rune-divider.svg';
// Render resolution for the recoloured ornament's offscreen canvas.
// Bigger than the on-canvas draw size so the final scale-down stays
// crisp. 1024 is plenty of headroom for a 500 px draw.
const ORNAMENT_TINT_RES = 1024;
// Sized to be visually prominent at each interior intersection.
// Lines no longer terminate at the intersections (each segment has
// a single centered dash with gaps at both ends), so the ornament
// has the full ~32% gap on each side of the intersection to occupy
// without crashing into the dashes. For our 5×4 grid that gap is
// ~480 canvas px per side; a 500 px ornament fills it comfortably
// while leaving breathing room before the dash.
const INTERSECTION_ORNAMENT_PX = 500;

// Licensed-mat corner seals (Collector Arthouse + the artist). Drawn in
// each player's BOTTOM-LEFT corner: the house seal for P1, the artist
// seal for P2 (rotated 180° to read upright from P2's seat). Size +
// margin are in canvas px (canvas is 10300×7200); the corner regions
// sit well outside the central grid so the seals never crowd play.
const CORNER_LOGO_PX = 520;
const CORNER_LOGO_MARGIN = 240;

// Deck-pile slot illustrations cover the same footprint as a single
// card +15% oversize so the slot art peeks out from under the
// physical card pile that lands on top of it. Spellbook + cemetery
// match a PORTRAIT card (cards are stored upright in those piles).
// Atlas matches a LANDSCAPE card. Atlas cards are played sideways
// on the board, so the slot illustration ships rotated too.
// Source-of-truth for card dimensions is cardMesh.js
// (CARD_WIDTH/HEIGHT × CARD_SCALE); duplicated here to avoid a
// circular dependency from cardMesh into the playmat UI pipeline.
const CARD_WORLD_WIDTH = 6.35 * 1.75;
const CARD_WORLD_HEIGHT = 8.89 * 1.75;
const SLOT_OVERSIZE_FACTOR = 1.15;
// Atlas reads small relative to the spellbook/cemetery slots, so its
// landscape illustration gets an extra bump on top of the shared
// oversize factor. Portrait slots are unaffected.
const ATLAS_SLOT_SCALE = 1.2;
const PORTRAIT_SLOT_CANVAS_W = (CARD_WORLD_WIDTH * SLOT_OVERSIZE_FACTOR) * (CANVAS_W / TABLE_WIDTH);
const PORTRAIT_SLOT_CANVAS_H = (CARD_WORLD_HEIGHT * SLOT_OVERSIZE_FACTOR) * (CANVAS_H / TABLE_HEIGHT);
// Landscape slot = portrait card laid on its side. World-space
// width/height are swapped; canvas conversion uses CANVAS_W/_H so
// the canvas-px result isn't a straight swap of portrait values.
const LANDSCAPE_SLOT_CANVAS_W = (CARD_WORLD_HEIGHT * SLOT_OVERSIZE_FACTOR * ATLAS_SLOT_SCALE) * (CANVAS_W / TABLE_WIDTH);
const LANDSCAPE_SLOT_CANVAS_H = (CARD_WORLD_WIDTH * SLOT_OVERSIZE_FACTOR * ATLAS_SLOT_SCALE) * (CANVAS_H / TABLE_HEIGHT);
const PORTRAIT_SLOT_FILES = new Set(['spellbook.svg', 'cemetery.svg']);
const LANDSCAPE_SLOT_FILES = new Set(['atlas.svg']);

const COMPONENT_BASE = `${getLocalApiOrigin()}/game-assets/playmats/components`;

const imageCache = new Map();
const textureCache = new Map();
// Cache of color-tinted ornament canvases keyed by `${url}|${color}`
// so we don't repeat the mask-fill composite on every re-bake.
const tintedImageCache = new Map();

function componentUrl(filename) {
  return `${COMPONENT_BASE}/${filename}`;
}

// Returns an offscreen canvas of `size`×`size` where every pixel
// is `color` with the source image's alpha as the mask.
//
// We softly amplify alpha (multiplier with cap) before tinting so
// the ornament reads with similar visual weight under both light
// and dark tints. The rune-divider SVG ships with drop-shadow +
// noise filters that produce a semi-transparent halo; a gentle
// boost lifts that halo enough to stay visible against any playmat
// background regardless of tint colour, without zeroing anything.
const ALPHA_AMP = 1.6;

function tintImage(img, color, size) {
  const key = `${img.src}|${color}|${size}`;
  const cached = tintedImageCache.get(key);
  if (cached) return cached;
  const off = document.createElement('canvas');
  off.width = size;
  off.height = size;
  const c = off.getContext('2d');
  c.imageSmoothingEnabled = true;
  c.imageSmoothingQuality = 'high';
  c.drawImage(img, 0, 0, size, size);

  const img2d = c.getImageData(0, 0, size, size);
  const px = img2d.data;
  for (let i = 3; i < px.length; i += 4) {
    const a = px[i];
    if (a === 0) continue;
    const boosted = a * ALPHA_AMP;
    px[i] = boosted >= 255 ? 255 : boosted | 0;
  }
  c.putImageData(img2d, 0, 0);

  c.globalCompositeOperation = 'source-in';
  c.fillStyle = color;
  c.fillRect(0, 0, size, size);
  tintedImageCache.set(key, off);
  return off;
}

function loadImage(url) {
  if (imageCache.has(url)) return imageCache.get(url);
  const promise = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${url}`));
    img.src = url;
  });
  imageCache.set(url, promise);
  return promise;
}

// World (x, z) → canvas (px, py). The mesh's UV maps shape coords to
// (px/W + 0.5, py/H + 0.5), and shape.y = -world.z after the -π/2 X
// rotation that flattens the mat. Combined with Three.js's
// flipY=true default on CanvasTexture (which flips canvas pixels
// vertically so OpenGL's bottom-origin convention matches DOM's
// top-origin), the round-trip is: canvas y=0 → world z=-70, canvas
// y=CANVAS_H → world z=+70. So canvas y grows with world z.
function worldToCanvas({ x, z }) {
  return {
    x: (x / TABLE_WIDTH + 0.5) * CANVAS_W,
    y: (0.5 + z / TABLE_HEIGHT) * CANVAS_H,
  };
}

function configHash(config) {
  const keys = Object.keys(config).sort();
  return keys.map((k) => {
    const v = config[k];
    // JSON-stringify objects so nested fields participate in the
    // cache key; otherwise `[object Object]` collapses every object
    // config into the same hash bucket.
    return `${k}=${v !== null && typeof v === 'object' ? JSON.stringify(v) : v}`;
  }).join('|');
}

function getVisualGrid() {
  // Real playmat grid lives in spawnConfig.gameGrid (the dev-
  // calibrated coords matching the painted grid on the mat).
  // DEFAULT_GRID is a fallback for the unlikely case it's missing.
  const conf = getSpawnConfigSync();
  return conf?.gameGrid || DEFAULT_GRID;
}

// Flatten the heterogeneous tracker shapes (some have ones/tens
// sub-rows, some are flat arrays of element-counter positions) into
// a single list of {x, z} points so we can compute a centroid.
function flattenTrackerPoints(tracker) {
  if (!tracker) return [];
  if (Array.isArray(tracker)) return tracker.slice();
  const out = [];
  for (const v of Object.values(tracker)) {
    if (Array.isArray(v)) out.push(...v);
    else if (v && typeof v === 'object') {
      for (const inner of Object.values(v)) {
        if (Array.isArray(inner)) out.push(...inner);
      }
    }
  }
  return out;
}

// For the affinity tracker each element (earth/water/fire/wind) has
// its own column of positions. The tracker SVG combines all four
// columns, so we union all of them when computing the centroid.
function getAffinityCentroid(playerTrackers) {
  const all = [];
  for (const key of ['earth', 'water', 'fire', 'wind']) {
    all.push(...flattenTrackerPoints(playerTrackers?.[key]));
  }
  return centroidOf(all);
}

function centroidOf(points) {
  if (!points.length) return null;
  let sx = 0;
  let sz = 0;
  for (const p of points) { sx += p.x; sz += p.z; }
  return { x: sx / points.length, z: sz / points.length };
}

// Derive default {x, z} positions for every key in COMPONENT_KEYS
// from the static spawn config. Slot illustrations use the matching
// spawn point; trackers use the centroid of their per-player button
// positions. The editor uses this as its initial state when no
// authored override exists yet for a key.
export function getDefaultComponentPlacements() {
  const conf = getSpawnConfigSync() || {};
  const t = conf.trackers || {};
  const out = {};
  // Slot keys mirror their spawnConfig field names (`atlas`,
  // `atlas2`, …). Direct lookup.
  for (const k of ['atlas', 'atlas2', 'spellbook', 'spellbook2', 'cemetery', 'cemetery2']) {
    if (conf[k]) out[k] = { x: conf[k].x, z: conf[k].z };
  }
  const lifeP1 = centroidOf(flattenTrackerPoints(t.p1?.life));
  if (lifeP1) out.lifeP1 = lifeP1;
  const lifeP2 = centroidOf(flattenTrackerPoints(t.p2?.life));
  if (lifeP2) out.lifeP2 = lifeP2;
  const manaP1 = centroidOf(flattenTrackerPoints(t.p1?.mana));
  if (manaP1) out.manaP1 = manaP1;
  const manaP2 = centroidOf(flattenTrackerPoints(t.p2?.mana));
  if (manaP2) out.manaP2 = manaP2;
  const affP1 = getAffinityCentroid(t.p1);
  if (affP1) out.affinityP1 = affP1;
  const affP2 = getAffinityCentroid(t.p2);
  if (affP2) out.affinityP2 = affP2;
  return out;
}

// Resolves the final {x, z} for a component key: authored override
// wins, falls back to spawn-config-derived default.
function effectivePlacement(key) {
  if (COMPONENT_PLACEMENTS[key]) {
    return { x: COMPONENT_PLACEMENTS[key].x, z: COMPONENT_PLACEMENTS[key].z };
  }
  const defaults = getDefaultComponentPlacements();
  return defaults[key] || null;
}

function buildComponentPlacements() {
  const out = [];
  for (const def of COMPONENT_KEYS) {
    if (def.disabled) continue;
    const pt = effectivePlacement(def.key);
    if (!pt) continue;
    out.push({
      file: def.file,
      center: pt,
      // P2 components rotate 180°, P1 stay upright. With the
      // corrected worldToCanvas mapping (canvas y grows with world
      // z) plus flipY=true: an unrotated SVG's "top" in canvas
      // (smaller y) ends up at world's MORE-NEGATIVE z direction on
      // the mesh. P1 sits at +z and looks toward -z, so -z is
      // P1's "top of view" → unrotated SVG reads upright for P1. P2
      // sits at -z and looks toward +z, so for P2 the -z direction
      // is "behind/bottom." The SVG needs a 180° rotation so its
      // top instead points to +z (P2's top of view).
      rotate: def.player === 2,
      opacityKey: def.toggleKey || null,
    });
  }
  return out;
}

function lerpPt(p1, p2, t) {
  return { x: p1.x + (p2.x - p1.x) * t, y: p1.y + (p2.y - p1.y) * t };
}

// Hand-drawn stroke parameters. Each logical line subdivides into
// waypoints with small perpendicular jitter, then we connect them
// with quadratic curves so the wobble reads as natural curvature
// rather than a polyline of kinks. Single pass at full opacity.
// stacking translucent passes creates a haloed/blurry look, not a
// drawn one. Values are in canvas px (canvas is 10300×7200) and
// tuned against INNER_LINE_WIDTH = 8.
const PENCIL_SEGMENT_PX = 28;       // distance between successive jittered waypoints
const PENCIL_JITTER_PX = 3.5;       // peak perpendicular wobble at mid-stroke
const PENCIL_END_JITTER_PX = 0.8;   // near-zero wobble at the endpoints (junctions stay tight)
const PENCIL_WIDTH_VARIATION = 0.18; // per-stroke line-width variation (relative to base width)

// Mulberry32-ish PRNG advanced by a mutable seed object. Seeded
// from segment endpoints so jitter is stable across re-bakes (same
// config hash → identical pixels, no shimmer).
function makeSeed(x1, y1, x2, y2) {
  let s = (Math.imul(Math.round(x1), 374761393) ^
           Math.imul(Math.round(y1), 668265263) ^
           Math.imul(Math.round(x2), 2147483647) ^
           Math.imul(Math.round(y2), 1664525)) | 0;
  s = Math.imul(s ^ (s >>> 13), 1274126177);
  return { s: (s ^ (s >>> 16)) | 0 };
}

function nextRand(seed) {
  seed.s = (seed.s + 0x6D2B79F5) | 0;
  let t = seed.s;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return (((t ^ (t >>> 14)) >>> 0) / 4294967296);
}

// Draws a hand-drawn line from p1 to p2. Generates jittered
// waypoints along the path, then connects them with quadratic
// curves using midpoints as on-curve anchors. Each waypoint pulls
// the curve as a control point, midpoints sit on the curve itself.
// Result: one smooth, gently-wandering stroke that reads as drawn-
// by-hand rather than a kinked polyline or a fuzzy multi-pass
// overlay. Caller controls ctx.strokeStyle and ctx.lineWidth; we
// slightly vary lineWidth per stroke for natural unevenness and
// restore it on exit.
function strokePencil(ctx, p1, p2) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy);
  if (len < 0.5) return;
  const nSeg = Math.max(3, Math.round(len / PENCIL_SEGMENT_PX));
  const nx = -dy / len; // unit perpendicular
  const ny = dx / len;
  const baseWidth = ctx.lineWidth;
  const seed = makeSeed(p1.x, p1.y, p2.x, p2.y);

  const pts = new Array(nSeg + 1);
  pts[0] = { x: p1.x, y: p1.y };
  pts[nSeg] = { x: p2.x, y: p2.y };
  for (let i = 1; i < nSeg; i++) {
    const t = i / nSeg;
    // Taper jitter to near-zero at the endpoints so consecutive
    // segments meeting at the same point still align cleanly.
    const taper = Math.sin(t * Math.PI); // 0 at ends, 1 at midpoint
    const jAmp = PENCIL_END_JITTER_PX + (PENCIL_JITTER_PX - PENCIL_END_JITTER_PX) * taper;
    const j = (nextRand(seed) - 0.5) * 2 * jAmp;
    pts[i] = { x: p1.x + dx * t + nx * j, y: p1.y + dy * t + ny * j };
  }

  ctx.lineWidth = baseWidth * (1 + (nextRand(seed) - 0.5) * PENCIL_WIDTH_VARIATION);
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < nSeg; i++) {
    const mx = (pts[i].x + pts[i + 1].x) / 2;
    const my = (pts[i].y + pts[i + 1].y) / 2;
    ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
  }
  ctx.lineTo(pts[nSeg].x, pts[nSeg].y);
  ctx.stroke();
  ctx.lineWidth = baseWidth;
}

// Two ticks at segment endpoints. Used for outer-border segments
// (both endpoints are corners / border T-junctions). Each tick
// extends `frac` of the segment length from its endpoint.
function drawTwoTicks(ctx, p1, p2, frac) {
  const a = lerpPt(p1, p2, frac);
  const b = lerpPt(p1, p2, 1 - frac);
  strokePencil(ctx, p1, a);
  strokePencil(ctx, b, p2);
}

// Single half-length tick emanating from one endpoint toward the
// other. The perpendicular "stick" from each border T-junction
// into the grid.
function drawHalfTick(ctx, from, to, frac) {
  const end = lerpPt(from, to, frac);
  strokePencil(ctx, from, end);
}

// Single dash centered at the segment midpoint. Used for inner
// segments. Both ornament-to-ornament and the centered piece in
// border-T-junction-to-ornament segments.
function drawCenteredDash(ctx, p1, p2, frac) {
  const a = lerpPt(p1, p2, 0.5 - frac / 2);
  const b = lerpPt(p1, p2, 0.5 + frac / 2);
  strokePencil(ctx, a, b);
}

function drawGrid(ctx, { uiColor, gridStyle, faded, thickLines }) {
  const grid = getVisualGrid();
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalC = colPositions.length - 1;
  const totalR = rowPositions.length - 1;
  const widthMul = thickLines ? THICK_LINE_MULT : 1;
  ctx.save();
  ctx.strokeStyle = uiColor;
  ctx.fillStyle = uiColor;
  ctx.globalAlpha = faded ? 0.45 : 1;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // OUTER BORDER. Two-tick pattern on each border sub-segment
  // (between corners / T-junctions along the border). The L-shape
  // at each corner and T at each border T-junction emerge from the
  // outward ticks on the adjacent border segments meeting there.
  ctx.lineWidth = OUTER_BORDER_WIDTH * widthMul;
  for (const r of [0, totalR]) {
    for (let c = 0; c < totalC; c++) {
      const p1 = worldToCanvas(getGridPoint(grid, colPositions[c], rowPositions[r]));
      const p2 = worldToCanvas(getGridPoint(grid, colPositions[c + 1], rowPositions[r]));
      drawTwoTicks(ctx, p1, p2, TICK_FRACTION);
    }
  }
  for (const c of [0, totalC]) {
    for (let r = 0; r < totalR; r++) {
      const p1 = worldToCanvas(getGridPoint(grid, colPositions[c], rowPositions[r]));
      const p2 = worldToCanvas(getGridPoint(grid, colPositions[c], rowPositions[r + 1]));
      drawTwoTicks(ctx, p1, p2, TICK_FRACTION);
    }
  }

  // T-JUNCTION INWARD STICKS. From every border T-junction, a
  // half-length stick extends perpendicular INTO the grid. Always
  // drawn regardless of dashed/dotted, because it's part of the
  // border T-shape, not the inner connecting lines.
  ctx.lineWidth = INNER_LINE_WIDTH * widthMul;
  for (let c = 1; c < totalC; c++) {
    // Top border T-junctions emanate downward
    const topFrom = worldToCanvas(getGridPoint(grid, colPositions[c], rowPositions[0]));
    const topTo   = worldToCanvas(getGridPoint(grid, colPositions[c], rowPositions[1]));
    drawHalfTick(ctx, topFrom, topTo, TICK_FRACTION_HALF);
    // Bottom border T-junctions emanate upward
    const botFrom = worldToCanvas(getGridPoint(grid, colPositions[c], rowPositions[totalR]));
    const botTo   = worldToCanvas(getGridPoint(grid, colPositions[c], rowPositions[totalR - 1]));
    drawHalfTick(ctx, botFrom, botTo, TICK_FRACTION_HALF);
  }
  for (let r = 1; r < totalR; r++) {
    // Left border T-junctions emanate rightward
    const leftFrom  = worldToCanvas(getGridPoint(grid, colPositions[0], rowPositions[r]));
    const leftTo    = worldToCanvas(getGridPoint(grid, colPositions[1], rowPositions[r]));
    drawHalfTick(ctx, leftFrom, leftTo, TICK_FRACTION_HALF);
    // Right border T-junctions emanate leftward
    const rightFrom = worldToCanvas(getGridPoint(grid, colPositions[totalC], rowPositions[r]));
    const rightTo   = worldToCanvas(getGridPoint(grid, colPositions[totalC - 1], rowPositions[r]));
    drawHalfTick(ctx, rightFrom, rightTo, TICK_FRACTION_HALF);
  }

  // INNER CENTERED DASHES. Dashed style only. One dash centered in
  // every inner segment (both ornament-to-ornament and border-T-
  // junction-to-ornament). No ticks emanating from ornaments, so
  // ornaments stay clean (no cross showing through them).
  if (gridStyle === 'dashed') {
    ctx.lineWidth = INNER_LINE_WIDTH * widthMul;
    for (let r = 1; r < totalR; r++) {
      for (let c = 0; c < totalC; c++) {
        const p1 = worldToCanvas(getGridPoint(grid, colPositions[c], rowPositions[r]));
        const p2 = worldToCanvas(getGridPoint(grid, colPositions[c + 1], rowPositions[r]));
        drawCenteredDash(ctx, p1, p2, CENTERED_DASH_FRACTION);
      }
    }
    for (let c = 1; c < totalC; c++) {
      for (let r = 0; r < totalR; r++) {
        const p1 = worldToCanvas(getGridPoint(grid, colPositions[c], rowPositions[r]));
        const p2 = worldToCanvas(getGridPoint(grid, colPositions[c], rowPositions[r + 1]));
        drawCenteredDash(ctx, p1, p2, CENTERED_DASH_FRACTION);
      }
    }
  }
  ctx.restore();
}

function drawCellNumbers(ctx, { uiColor, cellNumbers, faded, largeNumbers }) {
  if (cellNumbers === 'off') return;
  const grid = getVisualGrid();
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;
  // Large-numbers mode bumps the font up and switches to a heavier
  // weight so the glyphs read as deliberately bigger, not just scaled.
  const fontPx = Math.round(CELL_NUMBER_FONT_PX * (largeNumbers ? LARGE_NUMBER_MULT : 1));
  const fontWeight = largeNumbers ? '700 ' : '';

  ctx.save();
  ctx.globalAlpha = faded ? 0.45 : 1;
  ctx.font = `${fontWeight}${fontPx}px ${CELL_NUMBER_FONT_FAMILY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = uiColor;

  // Every cell carries the SAME number twice. Once near its
  // bottom-inside edge (upright, P1's near side) and once near its
  // top-inside edge rotated 180° (P2's near side from their seat).
  // Numbers are INSET from the grid lines by NUMBER_INSET_FRAC of
  // the cell height so they sit cleanly inside each square instead
  // of straddling the border line between cells.
  const NUMBER_INSET_FRAC = 0.12;
  for (let r = 0; r < totalRows; r++) {
    for (let c = 0; c < totalCols; c++) {
      const cellNumber = (totalRows - 1 - r) * totalCols + (totalCols - c);
      if (cellNumbers === 'sparse' && cellNumber % 2 === 0) continue;
      const centerColT = (colPositions[c] + colPositions[c + 1]) / 2;
      const topT = rowPositions[r];
      const botT = rowPositions[r + 1];
      // Lerp between top and bottom edge of the cell.
      const p1RowT = topT + (botT - topT) * (1 - NUMBER_INSET_FRAC); // near bottom, inside
      const p2RowT = topT + (botT - topT) * NUMBER_INSET_FRAC;       // near top, inside

      // P1. Near the cell's canvas-bottom edge, upright.
      const p1Pt = worldToCanvas(getGridPoint(grid, centerColT, p1RowT));
      ctx.save();
      ctx.translate(p1Pt.x, p1Pt.y);
      ctx.fillText(String(cellNumber), 0, 0);
      ctx.restore();

      // P2. Near the cell's canvas-top edge, rotated 180° so it
      // reads upright from P2's seat at the opposite side.
      const p2Pt = worldToCanvas(getGridPoint(grid, centerColT, p2RowT));
      ctx.save();
      ctx.translate(p2Pt.x, p2Pt.y);
      ctx.rotate(Math.PI);
      ctx.fillText(String(cellNumber), 0, 0);
      ctx.restore();
    }
  }
  ctx.restore();
}

// Draws a single SVG image centered on a world-coord point. When
// `rotate` is true the image is drawn rotated 180° around its
// center. Deck-pile slot illustrations (atlas, spellbook, cemetery)
// render at a fixed (SLOT_CANVAS_W × SLOT_CANVAS_H) size so all
// three are visually identical; everything else uses the SVG's
// natural dimensions.
async function drawComponentAt(ctx, { file, center, rotate, alpha }) {
  let img;
  try {
    img = await loadImage(componentUrl(file));
  } catch (err) {
    console.warn(`[uiOverlay] component ${file} failed to load:`, err.message);
    return;
  }
  const canvasPt = worldToCanvas(center);
  let w = img.naturalWidth;
  let h = img.naturalHeight;
  if (PORTRAIT_SLOT_FILES.has(file)) {
    w = PORTRAIT_SLOT_CANVAS_W;
    h = PORTRAIT_SLOT_CANVAS_H;
  } else if (LANDSCAPE_SLOT_FILES.has(file)) {
    w = LANDSCAPE_SLOT_CANVAS_W;
    h = LANDSCAPE_SLOT_CANVAS_H;
  }
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(canvasPt.x, canvasPt.y);
  if (rotate) ctx.rotate(Math.PI);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  ctx.restore();
}

// Fills the entire canvas with a tiled texture at the configured
// alpha. Used by themes that want a paper-grain feel across the
// whole mat surface (vanilla) instead of leaving the central play
// area visually flat. Renders BEFORE grid + numbers + ornaments so
// those sit cleanly on top of the grain.
async function drawTextureOverlay(ctx, { textureOverlay }) {
  if (!textureOverlay?.url) return;
  let texImg;
  try {
    texImg = await loadImage(textureOverlay.url);
  } catch (err) {
    console.warn('[uiOverlay] texture overlay failed to load:', err.message);
    return;
  }
  const pattern = ctx.createPattern(texImg, 'repeat');
  if (!pattern) return;
  ctx.save();
  ctx.globalAlpha = typeof textureOverlay.alpha === 'number' ? textureOverlay.alpha : 0.2;
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.restore();
}

async function drawIntersectionOrnaments(ctx, { faded, uiColor }) {
  let ornament;
  try {
    ornament = await loadImage(INTERSECTION_ORNAMENT_URL);
  } catch (err) {
    console.warn('[uiOverlay] intersection ornament failed to load:', err.message);
    return;
  }
  const grid = getVisualGrid();
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const half = INTERSECTION_ORNAMENT_PX / 2;
  // Recolour the ornament to match the grid colour so it always
  // reads as part of the same drawn line, regardless of light/dark
  // mode or the playmat's painted background.
  const tinted = tintImage(ornament, uiColor, ORNAMENT_TINT_RES);
  ctx.save();
  ctx.globalAlpha = faded ? 0.45 : 1;
  // Only INTERIOR intersections. Where four cells meet. Boundary
  // intersections (first/last column or row position) sit on the
  // grid's outer border and should stay clean per the Figma design.
  // For a 5×4 grid that's 4 interior columns × 3 interior rows = 12
  // ornaments.
  for (let ci = 1; ci < colPositions.length - 1; ci++) {
    for (let ri = 1; ri < rowPositions.length - 1; ri++) {
      const pt = worldToCanvas(getGridPoint(grid, colPositions[ci], rowPositions[ri]));
      ctx.drawImage(tinted, pt.x - half, pt.y - half, INTERSECTION_ORNAMENT_PX, INTERSECTION_ORNAMENT_PX);
    }
  }
  ctx.restore();
}

// Licensed mats carry their attribution seals baked into the corners.
// `cornerLogos.p1` (the house) goes in P1's bottom-left = canvas
// bottom-left (small x, large y), upright. `cornerLogos.p2` (the
// artist) goes in P2's bottom-left = the diagonally-opposite canvas
// top-right (large x, small y), rotated 180° so it reads upright from
// P2's seat. The same per-player orientation the cell numbers use.
async function drawCornerLogos(ctx, { cornerLogos, faded }) {
  if (!cornerLogos) return;
  const half = CORNER_LOGO_PX / 2;
  const alpha = faded ? 0.7 : 0.92;
  const placements = [
    cornerLogos.p1 ? { url: cornerLogos.p1, cx: CORNER_LOGO_MARGIN + half, cy: CANVAS_H - CORNER_LOGO_MARGIN - half, rotate: false } : null,
    cornerLogos.p2 ? { url: cornerLogos.p2, cx: CANVAS_W - CORNER_LOGO_MARGIN - half, cy: CORNER_LOGO_MARGIN + half, rotate: true } : null,
  ].filter(Boolean);
  for (const { url, cx, cy, rotate } of placements) {
    let img;
    try {
      img = await loadImage(url);
    } catch (err) {
      console.warn('[uiOverlay] corner logo failed to load:', err.message);
      continue;
    }
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(cx, cy);
    if (rotate) ctx.rotate(Math.PI);
    ctx.drawImage(img, -half, -half, CORNER_LOGO_PX, CORNER_LOGO_PX);
    ctx.restore();
  }
}

/**
 * Build a THREE.CanvasTexture for the playmat UI overlay matching
 * the given config. Result is cached by config hash.
 *
 * Config shape:
 *   uiColor: '#FFFFFF' | '#000000' | …  (recolors grid + numbers)
 *   gridStyle: 'dashed' | 'dotted'
 *   cellNumbers: 'off' | 'sparse' | 'all'
 *   faded: boolean
 *   showLifeTracker / showManaTracker / showAffinityTracker: boolean
 * Each toggles BOTH players' trackers; the trackers are
 *       symmetric so per-player toggles aren't useful here.
 */
export async function generatePlaymatUiTexture(config) {
  const hash = configHash(config);
  const existing = textureCache.get(hash);
  if (existing) return existing;

  // Wait for all @font-face fonts to load before painting glyphs,
  // otherwise the cell-number text quietly falls back to a system
  // serif (canvas doesn't warn on missing fonts, it just substitutes
  // silently and the result gets baked into the cached texture). On
  // a warm cache `document.fonts.ready` resolves immediately.
  if (typeof document !== 'undefined' && document.fonts?.ready) {
    try { await document.fonts.ready; } catch (_) {}
  }

  // All drawing code below works in the fixed 10300×7200 design space; the
  // canvas itself is allocated at the display-derived budget and a single
  // ctx.scale() maps design units onto it. Same output, no per-draw changes.
  const scale = playmatTexelBudget() / CANVAS_W;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(CANVAS_W * scale);
  canvas.height = Math.round(CANVAS_H * scale);
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);

  // Paper-grain texture (theme-driven) goes down first so component
  // slot illustrations + grid + numbers + ornaments all sit on top.
  await drawTextureOverlay(ctx, config);

  const alpha = config.faded ? 0.6 : 1;
  // While the in-game Component Editor is open, the 3D scene shows
  // each component as a live draggable mesh. We skip baking them
  // into the texture so the user only sees one copy (the draggable
  // mesh) rather than the static-baked-plus-mesh double-vision.
  if (!config.skipComponents) {
    const placements = buildComponentPlacements();
    for (const p of placements) {
      if (p.opacityKey && !config[p.opacityKey]) continue;
      await drawComponentAt(ctx, { ...p, alpha });
    }
  }

  drawGrid(ctx, config);
  drawCellNumbers(ctx, config);
  await drawIntersectionOrnaments(ctx, config);
  await drawCornerLogos(ctx, config);

  // Transfer the raster to an ImageBitmap and free the 2D canvas: a
  // CanvasTexture would pin the canvas's full CPU backing store for the
  // texture's whole life purely as an upload source. The bitmap is a single
  // (often GPU-backed) copy and serves as the re-upload source on context
  // restore. Pre-flip because UNPACK_FLIP_Y_WEBGL is ignored for bitmaps.
  const bitmap = await createImageBitmap(canvas, { imageOrientation: 'flipY' });
  canvas.width = 0;
  canvas.height = 0;
  const texture = new THREE.Texture(bitmap);
  texture.flipY = false;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  textureCache.set(hash, texture);
  return texture;
}

export function disposePlaymatUiTextures() {
  for (const tex of textureCache.values()) {
    tex.image?.close?.();
    tex.dispose();
  }
  textureCache.clear();
  imageCache.clear();
  tintedImageCache.clear();
}
